import useUpdateProject from '@/api/mutations/project/useUpdateProject';
import projectKeys from '@/api/queries/project/keyFactory';
import { ProjectByIdQuery } from '@/gql/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Input,
  Modal,
  ModalProps,
  Textarea,
} from '@payaca/ambrosia';

import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod/v4';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';
import { useSelector } from '../../../../../api/state';
import { getUserRoles } from '../../../../../utils/stateAccessors';
import { registerFieldLegacyBuilder } from '../../../../../utils/zod';

const editProjectSchema = z.object({
  reference: z.string().max(30).optional(),
  description: z.string().max(200).optional(),
});
type EditProjectSchema = z.infer<typeof editProjectSchema>;

const EditProjectModal: FC<Omit<ModalProps, 'title'>> = (props) => {
  const userRoles = useSelector(getUserRoles);
  const { ...rest } = props;
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { project } = useGetProjectByIdOverview(projectId);
  const { mutateAsync: updateProjectMutation } = useUpdateProject({
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOver = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          if (!variables.customReference && !variables.description) {
            // Should never get here
            return old;
          }

          return {
            ...old,
            projectById: {
              ...old.projectById,
              reference: variables.customReference || old.projectById.reference,
              description:
                variables.description?.length !== 0
                  ? variables.description
                  : null,
            },
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousProjectOver };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOver
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.project(projectId),
      });
    },
  });

  const formMethods = useForm<EditProjectSchema>({
    resolver: zodResolver(editProjectSchema),
    defaultValues: {
      reference: project?.reference || undefined,
      description: project?.description || undefined,
    },
  });
  const registerField = registerFieldLegacyBuilder(
    formMethods.formState.errors
  );

  const onSubmit = async (data: EditProjectSchema) => {
    await updateProjectMutation({
      projectId: projectId,
      customReference: data.reference || null,
      description: data.description?.length === 0 ? null : data.description,
    });
    rest.onClose?.();
  };

  return (
    <Modal title="Edit Project" {...rest}>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Modal.Body className="space-y-4">
            <Field.Legacy {...registerField('reference')}>
              <Field.Label>Project reference</Field.Label>
              <Controller
                name="reference"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Input
                      value={value}
                      onChange={onChange}
                      disabled={
                        !userHasRequiredPermission(userRoles, [
                          DealsPermissions.UPDATE_DEAL_REFERENCE,
                        ])
                      }
                    />
                  );
                }}
              />
            </Field.Legacy>
            <Field.Legacy {...registerField('description')}>
              <Field.Label>Project description</Field.Label>
              <Controller
                name="description"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Textarea
                      value={value}
                      onChange={onChange}
                      rows={2}
                      autoHeight
                      disabled={
                        !userHasRequiredPermission(userRoles, [
                          DealsPermissions.UPDATE_DEAL_DESCRIPTION,
                        ])
                      }
                    />
                  );
                }}
              />
            </Field.Legacy>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button type="submit">Save</Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditProjectModal;
